import {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";

import {
  addProjectUsersAPICall,
  createProjectAPICall,
  deleteProjectAPICall,
  disableProjectUsersAPICall,
  getProjectsAPICall,
  getProjectUsageAPICall,
  getProjectUsersAPICall,
  getRecentActivityAPICall,
} from "../api/projects/projectService";
import { getNumberOfConfirmedUsersAPICall } from "../api/licensing/licensingService";

export const ProjectContext = createContext({
  projects: [],
  selectedProject: [],
  selectedProjectRef: [],
  currentMembership: null,
  isAdmin: false,
  hasFullProductAccess: false,
  canAccessProduct: () => false,
  projectUsers: [],
  meetingsUsersCount: 0,
  teamsUsersCount: 0,
  setProjects: () => {},
  setSelectedProject: () => {},
  getProjects: () => {},
  createProject: () => {},
  getProjectUsage: () => {},
  getRecentActivity: () => {},
  deleteProject: () => {},
  setProjectUsers: () => {},
  getProjectUsers: () => {},
  addProjectUsers: () => {},
  disableProjectUsers: () => {},
});

const ProjectContextProvider = ({ children, setLoading }) => {
  const [projects, setProjects] = useState(undefined);
  // Internal state setter renamed to wrap persistence logic below
  const [selectedProject, _setSelectedProject] = useState(undefined);
  const [projectUsers, setProjectUsers] = useState([]);
  const [meetingsUsersCount, setMeetingsUsersCount] = useState(0);
  const [teamsUsersCount, setTeamsUsersCount] = useState(0);

  const selectedProjectRef = useRef(null);

  const currentMembership = useMemo(() => {
    if (!selectedProject) return null;
    const latest = projects?.find((p) => p.id === selectedProject.id) || selectedProject;
    if (!latest.role) return null;
    return { role: latest.role, product_slugs: latest.product_slugs || [] };
  }, [projects, selectedProject]);

  const isAdmin = currentMembership?.role === "admin";
  const hasFullProductAccess = !!currentMembership && (isAdmin || currentMembership.product_slugs.length === 0);
  const canAccessProduct = useCallback(
    (productSlug) => hasFullProductAccess || !!currentMembership?.product_slugs.includes(productSlug),
    [hasFullProductAccess, currentMembership]
  );

  useEffect(() => {
    setMeetingsUsersCount(0);
    setTeamsUsersCount(0);

    if (!selectedProject?.id) return;

    getNumberOfConfirmedUsersAPICall(
      selectedProject.id,
      (resp) => setMeetingsUsersCount(resp.data.confirmed_users),
      () => {},
      "meetings"
    );
    getNumberOfConfirmedUsersAPICall(
      selectedProject.id,
      (resp) => setTeamsUsersCount(resp.data.confirmed_users),
      () => {},
      "emotii_for_teams"
    );
  }, [selectedProject?.id]);

  const setSelectedProject = useCallback((project) => {
    _setSelectedProject(project);
    try {
      if (project && project.id) {
        localStorage.setItem("selectedProjectId", String(project.id));
      } else {
        localStorage.removeItem("selectedProjectId");
      }
    } catch (e) {
    }
  }, []);

  const getProjects = useCallback(async (onSuccess, onError) => {
    getProjectsAPICall(function (response) {
      setProjects(response.data.response);
      onSuccess(response);
    }, onError);
  });

  const createProject = useCallback(
    async (name, members, onSuccess, onError) => {
      createProjectAPICall(
        name,
        members,
        function (response) {
          setProjects(response.data.response);
          onSuccess(response);
        },
        onError
      );
    }
  );
  const deleteProject = useCallback(async (projectId, onSuccess, onError) => {
    deleteProjectAPICall(
      projectId,
      function (response) {
        onSuccess(response);
      },
      onError
    );
  });

  const getProjectUsage = useCallback(
    async (projectId, startDate, endDate, onSuccess, onError) => {
      getProjectUsageAPICall(
        projectId,
        startDate,
        endDate,
        function (response) {
          onSuccess(response);
        },
        onError
      );
    }
  );

  const getRecentActivity = useCallback(
    async (projectId, onSuccess, onError) => {
      getRecentActivityAPICall(
        projectId,
        function (response) {
          onSuccess(response);
        },
        onError
      );
    },
    []
  );

  const handleProjectresponse = (response) => {
    setProjects(response.data.project);
  };

  const getProjectUsers = useCallback(
    async (projectId, term, page, itemPerPage, status, productSlug, onSuccess, onError, sortParams) => {
      getProjectUsersAPICall(
        projectId,
        term,
        page,
        itemPerPage,
        status,
        productSlug || 'meetings',
        function (response) {
          setProjectUsers(response.data.response);
          onSuccess(response);
        },
        onError,
        sortParams
      );
    }
  );

  const addProjectUsers = useCallback(
    async (projectId, users, productSlug, onSuccess, onError) => {
      addProjectUsersAPICall(
        projectId,
        users,
        productSlug || 'meetings',
        function (response) {
          onSuccess(response);
        },
        onError
      );
    }
  );

  const disableProjectUsers = useCallback(
    async (projectId, users, productSlug, onSuccess, onError) => {
      disableProjectUsersAPICall(
        projectId,
        users,
        productSlug || 'meetings',
        function (response) {
          onSuccess(response);
        },
        onError
      );
    }
  );

  return (
    <ProjectContext.Provider
      value={{
        projects,
        setProjects,
        selectedProject,
        setSelectedProject,
        selectedProjectRef,
        currentMembership,
        isAdmin,
        hasFullProductAccess,
        canAccessProduct,
        getProjects,
        createProject,
        deleteProject,
        getProjectUsage,
        getRecentActivity,
        projectUsers,
        setProjectUsers,
        meetingsUsersCount,
        teamsUsersCount,
        getProjectUsers,
        addProjectUsers,
        disableProjectUsers,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => useContext(ProjectContext);

export default ProjectContextProvider;
